import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCreateIntradayPushSubscriptionMutation,
  useCreateRtaPushSubscriptionMutation,
  useGetAdherenceNotificationSetupQuery,
  useGetIntradayPushNotificationOnOffSettingQuery,
  useGetPublicVapidKeyQuery,
} from '../../graphql/components';
import { initializeServiceWorkerLocalization } from '../../utils/indexedDbUtils';
import {
  getNotificationPermission,
  isNotificationSupported,
  requestNotificationPermissions,
} from '../../utils/notificationUtils';
import {
  clientUnsubscribe,
  getOrCreatePushManagerSubscription,
  getOrRegisterServiceWorker,
  getPushManagerSubscription,
  getServiceWorkerRegistration,
  isServiceWorkerSupported,
} from '../../utils/swUtils';
import { Permissions, useAuth } from '../AuthorizationProvider';
import { useConfig } from '../ConfigurationProvider';

import type { NotificationType, PushNotificationProviderState } from './types';

const initialNotifications = {
  intraday: false,
  rta: false,
};

export const PushNotificationContext = React.createContext({} as unknown as PushNotificationProviderState);

const usePushNotificationData = () => {
  const { userHasPermissions } = useAuth();

  const canReceiveRTA = userHasPermissions([Permissions.ViewRealTimeAdherenceEvents]);

  const canReceiveIntraday = userHasPermissions([Permissions.ViewIntradayEvents]);

  const { data: adherenceNotificationData, loading: adherenceNotificationLoading } =
    useGetAdherenceNotificationSetupQuery({
      fetchPolicy: 'network-only',
      skip: !canReceiveRTA,
    });

  const { data: intradayNotificationData, loading: intradayNotificationLoading } =
    useGetIntradayPushNotificationOnOffSettingQuery({
      fetchPolicy: 'network-only',
      skip: !canReceiveIntraday,
    });

  const isInitialLoad = adherenceNotificationLoading || intradayNotificationLoading;
  const hasPermissions = canReceiveRTA || canReceiveIntraday;
  const notifications: PushNotificationProviderState['notifications'] = {
    rta: typeof adherenceNotificationData?.adherenceNotificationSetup?.threshold === 'number',
    intraday: intradayNotificationData?.intradayPushNotificationOnOffSetting?.enabled ?? false,
  };

  if (isInitialLoad) {
    return {
      isInitialLoad,
      hasPermissions,
      notifications: initialNotifications,
    };
  }

  return {
    isInitialLoad,
    hasPermissions,
    notifications,
  };
};

/**
 * Updates push subscription on BE, if it does not exists create new one.
 */
export const usePushNotificationSubscriptions = () => {
  const { serviceWorkerUrl } = useConfig();
  const [createRtaPushSubscriptionMutation] = useCreateRtaPushSubscriptionMutation();
  const [createIntradayPushSubscriptionMutation] = useCreateIntradayPushSubscriptionMutation();

  const getSubscriptionVariables = async (publicVapidKey: string) => {
    const registration = await getOrRegisterServiceWorker(serviceWorkerUrl);
    const pushSubscription = await getOrCreatePushManagerSubscription(registration, publicVapidKey);
    const pushSubscriptionJSON = pushSubscription.toJSON();
    const { keys, endpoint, expirationTime } = pushSubscriptionJSON;

    return {
      subscription: {
        endpoint: endpoint!,
        auth: keys!.auth,
        p256dh: keys!.p256dh,
        expirationTime: expirationTime ? new Date(expirationTime).toISOString() : null,
      },
    };
  };

  const subscribe = async (notification: NotificationType, publicVapidKey: string) => {
    const variables = await getSubscriptionVariables(publicVapidKey);

    switch (notification) {
      case 'intraday':
        return createIntradayPushSubscriptionMutation({ variables });
      case 'rta':
        return createRtaPushSubscriptionMutation({ variables });
      default:
        throw new Error(`[PushNotificationProvider]: Notification type "${notification}" is not recognized.`);
    }
  };

  return { subscribe };
};

export const PushNotificationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { locale } = useAuth();
  const { t } = useTranslation();

  const { isInitialLoad, notifications, hasPermissions } = usePushNotificationData();
  const isInitialized = React.useRef(false);
  const previousNotifications = React.useRef<typeof notifications>(initialNotifications);

  const { data: dataPublicVapidKey } = useGetPublicVapidKeyQuery({ skip: !hasPermissions });
  const publicVapidKey = dataPublicVapidKey?.publicVapidKey?.publicKey;

  const isPushNotificationsSupported = isNotificationSupported() && isServiceWorkerSupported();

  const subscriptions = usePushNotificationSubscriptions();

  const subscribe = useCallback(
    async (notification: NotificationType, silentPermissionsCheck = false) => {
      if (!isPushNotificationsSupported) {
        // eslint-disable-next-line no-console
        console.warn('[PushNotificationProvider]: Browser does not support PUSH notifications or service workers!');
        return false;
      }

      if (!hasPermissions) {
        // eslint-disable-next-line no-console
        console.warn('[PushNotificationProvider]: No permissions');
        return false;
      }

      if (!publicVapidKey) {
        // eslint-disable-next-line no-console
        console.warn('[PushNotificationProvider]: Subscribe request ignored, server public key missing.');
        return false;
      }

      if (!notifications[notification]) {
        // eslint-disable-next-line no-console
        console.warn('[PushNotificationProvider]: Subscribe request ignored, push notifications are disabled.');
        return false;
      }

      try {
        if (silentPermissionsCheck) {
          if (getNotificationPermission() !== 'granted') {
            throw new Error();
          }
        } else {
          await requestNotificationPermissions();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[PushNotificationProvider]: Subscribe request ignored, insufficient permissions.');
        return false;
      }

      try {
        await subscriptions.subscribe(notification, publicVapidKey);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          `[PushNotificationProvider]: Failed to create PUSH notification subscription for ${notification} with following error`,
          e
        );

        return false;
      }

      return true;
    },
    [hasPermissions, isPushNotificationsSupported, notifications, publicVapidKey, subscriptions]
  );

  const unsubscribe = async () => {
    const activeRegistration = await getServiceWorkerRegistration();
    const activeSubscription = activeRegistration && (await getPushManagerSubscription(activeRegistration));

    if (!activeSubscription) {
      return true;
    }

    return clientUnsubscribe(activeSubscription);
  };

  // Observe notifications backend data and fire subscribe/unsubscibe when changed
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    if (!isPushNotificationsSupported) {
      // eslint-disable-next-line no-console
      console.warn('[PushNotificationProvider]: Browser does not support PUSH notifications or service workers!');
      return;
    }

    if (!hasPermissions) {
      // eslint-disable-next-line no-console
      console.warn('[PushNotificationProvider]: No permissions');
      return;
    }

    if (!publicVapidKey) {
      // eslint-disable-next-line no-console
      console.warn('[PushNotificationProvider]: Subscribe request ignored, server public key missing.');
      return;
    }

    if (!isInitialized.current) {
      initializeServiceWorkerLocalization([
        {
          type: 'RtaAgentOoaNotification',
          locale,
          url: '/wfm/schedule-adherence/real-time-adherence/{{agent.agentId}}',
          body: t('scheduleAdherence.realTimeAdherence.pushNotification.body'),
          title: t('scheduleAdherence.realTimeAdherence.pushNotification.title'),
        },
        {
          type: 'IntradayQueueExceedThreshold',
          locale,
          url: '/wfm/intraday/{{queueId}}',
          body: t('intraday.pushNotification.outOfRange.body'),
          title: t('intraday.pushNotification.outOfRange.title'),
        },
      ]);
    }

    const notificationKeys = Object.keys(notifications) as NotificationType[];

    // Unsubscribe when no notification is enabled
    if (notificationKeys.filter((notification) => notifications[notification]).length === 0) {
      unsubscribe();

      isInitialized.current = true;
      previousNotifications.current = notifications;
      return;
    }

    // Create subscription only to newly enabled notifications (we do not unsubscribe per notification as of now)
    notificationKeys
      .filter(
        (notification) =>
          notifications[notification] === true &&
          notifications[notification] !== previousNotifications.current[notification]
      )
      .forEach((notification) => subscribe(notification, !isInitialized.current));

    isInitialized.current = true;
    previousNotifications.current = notifications;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermissions, isInitialLoad, isPushNotificationsSupported, notifications, publicVapidKey, subscribe]);

  const value = { notifications, subscribe };

  return <PushNotificationContext.Provider value={value}>{children}</PushNotificationContext.Provider>;
};

export const PushNotificationConsumer = PushNotificationContext.Consumer;

export const usePushNotifications = () => React.useContext(PushNotificationContext);
